import React, { Component, ErrorInfo, ReactNode } from 'react';
import i18n from '../i18n/i18n';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Last line of defence for render errors: without this, any uncaught error in
 * a component tree white-screens the whole app. Class component because React
 * only supports error boundaries via componentDidCatch/getDerivedStateFromError.
 *
 * Translation is looked up defensively (not via useTranslation) so the fallback
 * still renders even if the crash originated inside the i18n provider itself.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', {
      route: window.location.hash || window.location.pathname,
      error: error.message,
      componentStack: errorInfo.componentStack,
    });
  }

  private t(key: string, fallback: string): string {
    try {
      const value = i18n.t(key);
      return value && value !== key ? value : fallback;
    } catch {
      return fallback;
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/#/';
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="error-boundary-page">
        <div className="error-boundary-card">
          <div className="error-boundary-icon" aria-hidden="true">⚠</div>
          <h1 className="error-boundary-title">
            {this.t('errorBoundary.title', 'Something went wrong')}
          </h1>
          <p className="error-boundary-message">
            {this.t(
              'errorBoundary.message',
              'An unexpected error occurred while displaying this page. You can reload the application or return to the home page.'
            )}
          </p>
          {this.state.error && (
            <p className="error-boundary-detail">{this.state.error.message}</p>
          )}
          <div className="error-boundary-actions">
            <button className="ims-btn ims-btn-primary" onClick={this.handleReload}>
              {this.t('errorBoundary.reload', 'Reload')}
            </button>
            <button className="ims-btn ims-btn-secondary" onClick={this.handleGoHome}>
              {this.t('errorBoundary.goHome', 'Go to home')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
